import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Calendar, BedDouble, Building2, Check, ArrowLeft, Heart, Instagram, Phone, Bitcoin, Gift, Download, MessageCircle, FileText, Mail, Maximize2, Sofa } from 'lucide-react';
import ProjectMap from '../components/ProjectMap';
import { useApp } from '../context/AppContext';
import { openEnquiry } from '../components/EnquiryModal';
import PdfShare from '../components/PdfShare';
import { useTranslated } from '../hooks/useTranslated';
import { Button } from '../components/ui/button';

const ProjectDetail = () => {
  const { id } = useParams();
  const { t, formatPrice, shortlist, toggleShortlist, projects, settings, loaded } = useApp();
  const project = projects.find((p) => p.id === id);
  const [activeImg, setActiveImg] = useState(0);
  const companyInfo = settings;
  const [translatedDesc] = useTranslated(project ? [project.description] : ['']);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (project) document.title = `${project.name} | GOBRA Capital Real Estate`;
  }, [project]);

  if (!project) {
    if (!loaded) {
      return (
        <main className="pt-32 min-h-screen flex items-center justify-center" data-testid="project-loading">
          <div className="h-10 w-10 rounded-full border-2 border-gobra-sand border-t-gobra-green animate-spin" />
        </main>
      );
    }
    return (
      <main className="pt-32 min-h-screen text-center">
        <p className="text-gobra-slate">Project not found.</p>
        <Link to="/projects" className="text-gobra-green underline mt-4 inline-block">Back to projects</Link>
      </main>
    );
  }

  const gallery = [project.image, ...(project.gallery || [])].filter(Boolean);
  const saved = shortlist.includes(project.id);
  const offerActive = project.offer && (!project.offerExpiry || new Date(project.offerExpiry) >= new Date(new Date().toDateString()));
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/projects/${project.id}/pdf`;
  const shareText = `${project.name} by ${project.developer} — ${project.area}, Dubai. View the details PDF from GOBRA Capital: ${pdfUrl}`;
  const waShareUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const mailShareUrl = `mailto:?subject=${encodeURIComponent(`${project.name} — GOBRA Capital`)}&body=${encodeURIComponent(shareText)}`;
  const ytId = (() => {
    const u = project.youtubeUrl || '';
    const m = u.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
    return m ? m[1] : '';
  })();

  return (
    <main className="pt-24 md:pt-28 pb-20">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-gobra-green hover:text-gobra-terracotta mb-4">
          <ArrowLeft className="h-4 w-4" /> {t('view_all_projects')}
        </Link>

        {/* Gallery */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-3 rounded-xl overflow-hidden aspect-[16/10] bg-gobra-sand">
            {gallery[activeImg] && <img src={gallery[activeImg]} alt={project.name} className="h-full w-full object-cover" />}
          </div>
          <div className="lg:col-span-1 grid grid-cols-4 lg:grid-cols-1 gap-3">
            {gallery.slice(0, 4).map((g, i) => (
              <button key={`${g}-${i}`} onClick={() => setActiveImg(i)}
                className={`rounded-lg overflow-hidden aspect-square lg:aspect-[16/10] border-2 transition-colors ${activeImg === i ? 'border-gobra-terracotta' : 'border-transparent'}`}>
                <img src={g} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
          {/* Main */}
          <div className="lg:col-span-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="inline-block text-gobra-terracotta text-xs font-semibold tracking-widest uppercase">{project.developer}</span>
                <h1 className="font-title text-3xl md:text-4xl font-bold text-gobra-slate mt-1">{project.name}</h1>
                <div className="flex items-center gap-1.5 text-muted-foreground mt-2"><MapPin className="h-4 w-4 text-gobra-terracotta" /> {project.area}, Dubai</div>
              </div>
              <button onClick={() => toggleShortlist(project.id)} aria-label="Shortlist"
                className="h-11 w-11 rounded-full border border-gobra-sand flex items-center justify-center hover:border-gobra-terracotta transition-colors shrink-0">
                <Heart className={`h-5 w-5 ${saved ? 'fill-gobra-terracotta text-gobra-terracotta' : 'text-gobra-slate'}`} />
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              {[
                { icon: Calendar, label: t('handover_in'), value: project.handover },
                { icon: BedDouble, label: t('bedrooms'), value: project.beds },
                { icon: Building2, label: 'Type', value: project.type },
                ...(project.sqft ? [{ icon: Maximize2, label: 'Area (SQFT)', value: project.sqft }] : []),
                ...(project.furnishing ? [{ icon: Sofa, label: 'Furnishing', value: project.furnishing }] : []),
                { icon: Check, label: t('payment_plan'), value: `${project.plan}%` },
              ].filter((item) => item.value).map((item) => (
                <div key={item.label} className="bg-white border border-gobra-sand rounded-xl p-4">
                  <item.icon className="h-5 w-5 text-gobra-green" />
                  <div className="text-[11px] text-muted-foreground uppercase tracking-wide mt-2">{item.label}</div>
                  <div className="font-title font-semibold text-gobra-slate">{item.value}</div>
                </div>
              ))}
            </div>

            {offerActive && (
              <div data-testid="project-offer-banner" className="mt-6 rounded-xl border border-gobra-terracotta/30 bg-gobra-terracotta/5 p-5 flex items-start gap-4">
                <span className="h-11 w-11 rounded-full bg-gobra-terracotta text-white flex items-center justify-center shrink-0">
                  <Gift className="h-5 w-5" />
                </span>
                <div>
                  <div className="font-title text-lg font-semibold text-gobra-slate">Limited Launch Offer</div>
                  <p className="text-sm text-gobra-slate/80 mt-0.5">{project.offer}</p>
                  {project.offerExpiry && (
                    <p className="text-xs text-gobra-terracotta font-semibold mt-1.5 uppercase tracking-wide">
                      Offer valid until {new Date(project.offerExpiry).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mt-8">
              <h2 className="font-title text-2xl font-bold text-gobra-slate mb-3">{t('overview')}</h2>
              <p className="text-muted-foreground leading-relaxed">{translatedDesc || project.description}</p>
            </div>

            <div className="mt-8" data-testid="project-location-section">
              <h2 className="font-title text-2xl font-bold text-gobra-slate mb-1 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-gobra-terracotta" /> Location
              </h2>
              <p className="text-muted-foreground mb-3">{project.area ? `${project.area}, ` : ''}Dubai & Abu Dhabi — explore nearby landmarks and attractions.</p>
              <ProjectMap project={project} />
            </div>

            {(ytId || project.videoUrl) && (
              <div className="mt-8">
                <h2 className="font-title text-2xl font-bold text-gobra-slate mb-3">{t('video_tour')}</h2>
                <div className="relative w-full overflow-hidden rounded-xl border border-gobra-sand bg-black" style={{ aspectRatio: '16 / 9' }}>
                  {project.videoUrl ? (
                    <video
                      src={project.videoUrl}
                      controls
                      playsInline
                      poster={project.image}
                      className="absolute inset-0 h-full w-full object-cover"
                      data-testid="project-video-player"
                    />
                  ) : (
                    <iframe
                      src={`https://www.youtube.com/embed/${ytId}`}
                      title={`${project.name} video tour`}
                      className="absolute inset-0 h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )}
                </div>
              </div>
            )}

            <div className="mt-8">
              <h2 className="font-title text-2xl font-bold text-gobra-slate mb-4">{t('amenities')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {project.amenities.map((a) => (
                  <div key={a} className="flex items-center gap-2.5 text-gobra-slate">
                    <span className="h-6 w-6 rounded-full bg-gobra-green/10 flex items-center justify-center shrink-0">
                      <Check className="h-3.5 w-3.5 text-gobra-green" />
                    </span>
                    {a}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sticky sidebar */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 bg-white border border-gobra-sand rounded-2xl p-6 shadow-[0_20px_50px_-30px_rgba(47,61,76,0.4)]">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">{t('starting_from')}</div>
              <div className="font-title text-3xl font-bold text-gobra-green">{formatPrice(project.priceAed)}</div>
              <div className="text-sm text-muted-foreground mt-1">{project.plan}% {t('payment_plan')}</div>
              {project.acceptsUsdt && (
                <div className="mt-3 inline-flex items-center gap-1.5 bg-gobra-green/10 text-gobra-green text-xs font-semibold px-3 py-1.5 rounded-full">
                  <Bitcoin className="h-3.5 w-3.5" /> {t('usdt_accepted')}
                </div>
              )}

              <div className="mt-6 space-y-3">
                <Button onClick={() => openEnquiry(project.name)}
                  className="w-full bg-gobra-terracotta hover:bg-gobra-green text-white rounded-full py-6 text-base">
                  {t('register_interest')}
                </Button>
                <Button onClick={() => openEnquiry(`Viewing: ${project.name}`)} variant="outline"
                  className="w-full border-gobra-green text-gobra-green hover:bg-gobra-green hover:text-white rounded-full py-6 text-base">
                  {t('book_viewing')}
                </Button>
                {companyInfo.whatsapp && (
                  <Button asChild className="w-full bg-[#25D366] hover:bg-[#1ebe57] text-white rounded-full py-6 text-base">
                    <a href={`https://wa.me/${(companyInfo.whatsapp || '').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hello GOBRA Capital, I'm interested in ${project.name} (${project.area}). Please share more details.`)}`}
                      target="_blank" rel="noopener noreferrer" data-testid="whatsapp-project-btn">
                      <MessageCircle className="h-5 w-5 mr-2" /> {t('whatsapp_us')}
                    </a>
                  </Button>
                )}
                {project.brochureUrl ? (
                  <Button asChild variant="ghost" className="w-full text-gobra-slate hover:bg-gobra-sand rounded-full">
                    <a href={project.brochureUrl} download target="_blank" rel="noopener noreferrer" data-testid="download-brochure-link">
                      <Download className="h-4 w-4 mr-2" /> {t('download_brochure')}
                    </a>
                  </Button>
                ) : (
                  <Button onClick={() => openEnquiry(`Brochure: ${project.name}`)} variant="ghost"
                    className="w-full text-gobra-slate hover:bg-gobra-sand rounded-full">
                    {t('download_brochure')}
                  </Button>
                )}
              </div>

              <div className="border-t border-gobra-sand mt-6 pt-5">
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-3">{t('share_project')}</p>
                <PdfShare project={project} pdfUrl={pdfUrl} waShareUrl={waShareUrl} mailShareUrl={mailShareUrl} t={t} />
              </div>

              <div className="border-t border-gobra-sand mt-6 pt-5 space-y-3 text-sm">
                <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-gobra-slate hover:text-gobra-green">
                  <Phone className="h-4 w-4 text-gobra-terracotta" /> {companyInfo.phone}
                </a>
                <a href={companyInfo.instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gobra-slate hover:text-gobra-green">
                  <Instagram className="h-4 w-4 text-gobra-terracotta" /> {companyInfo.instagramHandle}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ProjectDetail;
